import { useState } from "react";

import { RichContentRenderer } from "@/components/helpers/ui/RichContentRenderer";
import { OneOneSession } from "@/components/pages_logged_in/program/components/program-component/panels/one-one-coaching/OneOneSession";
import { MeetingBasics } from "@/components/pieces/coaching/meetings/MeetingBasics";
import { ToggleField } from "@/components/pieces/form/ToggleField";
import { INNER_BORDER_COLOR } from "@/css/constants";
import { ComponentStatus, MeetingStatus } from "@/graphql";
import { PanelProps, ParticipantComponent } from "@/types/programs";

export function OneOneSessionItemPanel({
  component,
  view = "list",
}: PanelProps) {
  const { participantGuidance, meetings } = component as ParticipantComponent;
  const [showCancelMeeting, setShowCancelMeeting] = useState(false);
  const isUpcoming = component?.status === ComponentStatus.Upcoming;

  // Return null if no meetings
  if (!meetings || meetings.length === 0) {
    return null;
  }

  const onToggleChange = () => {
    setShowCancelMeeting(!showCancelMeeting);
  };

  const filteredMeetings = showCancelMeeting
    ? meetings
    : meetings?.filter((meeting) => !meeting.canceledAt);

  const hasCanceledMeetings = meetings?.some((meeting) => meeting.canceledAt);

  if (view === "feed") {
    const liveMeeting = filteredMeetings.find((meeting) =>
      [MeetingStatus.Ongoing, MeetingStatus.Soon].includes(
        meeting.status as MeetingStatus,
      ),
    );

    return liveMeeting ? (
      <div className="xs:p-3.5 xs:rounded-md xs:gap-3 flex flex-col bg-gradient-to-br from-[#F2FAFF] to-[#D8ECF8] md:gap-4 md:rounded-lg md:p-6">
        <div className="text-lg font-semibold text-blue-800">
          You have a live coaching session
        </div>
        <div
          className="round-corners xs:p-5 bg-white hover:bg-blue-50 md:p-6"
          style={{
            border: `1px solid ${INNER_BORDER_COLOR}`,
          }}
        >
          <MeetingBasics
            meeting={liveMeeting}
            chatChannelId={liveMeeting?.chatChannelId}
            large={false}
            showMessageButton={true}
            customerId={String(liveMeeting?.customerId)}
          />
        </div>
      </div>
    ) : null;
  }

  return (
    <div className="xs:gap-3.5 flex flex-col md:gap-4 empty:hidden">
      {participantGuidance && isUpcoming && (
        <RichContentRenderer content={participantGuidance} />
      )}

      {filteredMeetings?.length > 0 && (
        <div className="xs:gap-2.5 flex flex-col md:gap-3">
          {filteredMeetings.map((meeting) => (
            <OneOneSession
              meeting={meeting}
              component={component}
              key={meeting?.id}
            />
          ))}
        </div>
      )}

      {hasCanceledMeetings && (
        <>
          <hr className="m-0 border-neutral-300 first:hidden" />
          <div className="flex items-center">
            <div className="flex flex-1 items-center justify-end">
              <ToggleField
                checked={!showCancelMeeting}
                onChange={onToggleChange}
                size={12}
                label="Hide Canceled Sessions"
              ></ToggleField>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
